import React, { useState, useEffect } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useLocation } from "react-router-dom";
import * as yup from "yup";

import { PRIVATE_ROUTES } from "../../../constants";
import { PurchaseOrderProvider } from "../../../context/PurchaseOrderContext";
import { useAppState } from "../../../context/StateContext";
import { SupplierProvider } from "../../../context/SupplierContext";
import useActionAutomaticSequence from "../../../hooks/useActionAutomaticSequence";
import useActionPurchaseOrder from "../../../hooks/useActionPurchaseOrder";
import useYupValidation from "../../../hooks/useYupValidation";
import { AutomaticSequencing } from "../../../models/automatic.sequencing";
import { AutomaticSequenceOptions, CompanyProfile, OrderDetailsFields } from "../../../util/enums";
import OrderSSCC from "../../Generic/CartonSSCC";
import { CartonSSCCFrom } from "../../Generic/CartonSSCC/util";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "../../Generic/UITabMaterial";
import TitlePage from "../../TitlePage";
import { usePurchaseOrders } from "../hooks/usePurchaseOrders";
import StatusBadge from "../StatusBadge";
import { OrderTabs } from "../utils";
import OrderActionButtons from "./components/OrderActionButtons";
import Details, { SkeletonDetails } from "./Details";
import Products, { SkeletonProducts } from "./Products";
import useStyles from "./styles";

interface LocationProps {
  viewOnly: boolean;
}

const OrderMaintenanceScreen = () => {
  const { state } = useLocation<LocationProps>();
  const { id: purchaseOrderId } = useParams<{
    id?: string | undefined;
  }>();
  const { t: translate } = useTranslation();
  const { currentUser, isFMVersion } = useAppState();
  const classes = useStyles();
  const history = useHistory();

  const [viewOnly, setViewOnly] = useState<boolean>(false);
  const [supplierView, setSupplierView] = useState<boolean>(false);
  const [supplyChainView, setSupplyChainView] = useState<boolean>(false);
  const [sequence, setSequence] = useState<AutomaticSequencing>();
  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(OrderTabs.DETAILS);
  const {
    currenciesResponse,
    isDestinationPortFromBrazil,
    loadingOrderCartons,
    isLoadingPorts,
    portCodes,
    fetchingPurchaseOrder,
    cartons,
    purchaseOrder,
    handleUpdatePurchaseOrder,
    setCartons,
  } = usePurchaseOrders({ viewOnly, purchaseOrderId });
  const { useSavePurchaseOrder, getPurchaseOrderStatuses, useDeletePurchaseOrder } =
    useActionPurchaseOrder();

  const { mutate: deletePurchaseOrder, isLoading: deletingPurchaseOrder } =
    useDeletePurchaseOrder();

  const { getAutomaticSequenceByType } = useActionAutomaticSequence();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);

  getAutomaticSequenceByType(AutomaticSequenceOptions.ORDER, !purchaseOrder?.id, setSequence);
  const { data: statuses, isFetching: fetchingStatuses } = getPurchaseOrderStatuses();

  useEffect(() => {
    if (state) setViewOnly(state.viewOnly);
    if (currentUser?.company?.profile?.initials === CompanyProfile.SPP) setSupplierView(true);
    if (currentUser?.company?.profile?.initials === CompanyProfile.SP) setSupplyChainView(true);
  }, [state]);

  const { mutate: savePurchaseOrder, isLoading: loadingSavePurchaseOrder } =
    useSavePurchaseOrder(sequence);

  const schema = yup.object().shape({
    reference: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    consignee: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    originPort: yup
      .number()
      .nullable()
      .when("isFMVersion", {
        is: false,
        then: yup
          .number()
          .typeError(translate("thisFieldIsRequired"))
          .required(translate("thisFieldIsRequired")),
      }),
    destinationPort: yup
      .number()
      .nullable()
      .when("isFMVersion", {
        is: false,
        then: yup
          .number()
          .typeError(translate("thisFieldIsRequired"))
          .required(translate("thisFieldIsRequired")),
      }),
    supplier: yup
      .object()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    type: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    performancePrediction: yup
      .string()
      .nullable()
      .when("isFMVersion", {
        is: true,
        then: yup
          .string()
          .typeError(translate("thisFieldIsRequired"))
          .required(translate("thisFieldIsRequired")),
      }),
    currentEta: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    date: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    mode: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    projectId: yup
      .string()
      .nullable()
      .when("isFMVersion", {
        is: true,
        then: yup
          .string()
          .typeError(translate("thisFieldIsRequired"))
          .required(translate("thisFieldIsRequired")),
      }),
    products: yup.array().test((products) => {
      if (!products || products?.length < 1) {
        NotificationToast.warning(translate("atLeastOneProductIsRequired"));

        return false;
      }

      return true;
    }),
  });

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField, someFieldHasError } = useYupValidation(errors);

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          isFMVersion,
          reference: purchaseOrder?.reference,
          consignee: purchaseOrder?.consigneeId,
          currentEta: purchaseOrder?.currentEta,
          date: purchaseOrder?.date,
          mode: purchaseOrder?.mode,
          supplier: purchaseOrder?.supplier,
          originPort: purchaseOrder?.originPortId,
          destinationPort: purchaseOrder?.destinationPortId,
          type: purchaseOrder?.type,
          performancePrediction: purchaseOrder?.performancePrediction,
          projectId: purchaseOrder?.projectId,
          products: purchaseOrder?.purchaseOrderProducts,
        },
        { abortEarly: false }
      );
      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    return true;
  };

  const validateAndSavePurchaseOrder = () => {
    if (!purchaseOrder) return;

    if (!handleValidate()) return;

    savePurchaseOrder({ id: purchaseOrder.id, purchaseOrder });
  };

  const isUpdatingAction: boolean = loadingSavePurchaseOrder;

  const onCancel = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace(PRIVATE_ROUTES.ORDERS);
    }
  };

  const detailsPanel: PanelProps = {
    menuItem: translate("details"),
    className: classes.uiTab,
    key: "details",
    loading: fetchingPurchaseOrder || fetchingStatuses,
    skeleton: () => <SkeletonDetails />,
    hasError: someFieldHasError(Object.values(OrderDetailsFields)),
    render: () => (
      <Details
        purchaseOrder={purchaseOrder}
        updateOrder={handleUpdatePurchaseOrder}
        viewOnly={viewOnly}
        supplierView={supplierView}
        supplyChainView={supplyChainView}
        fieldHasError={fieldHasError}
        getErrorByField={getErrorByField}
        sequence={sequence}
        statuses={statuses}
        portCodes={portCodes}
        loadingPorts={isLoadingPorts}
        deletePurchaseOrder={deletePurchaseOrder}
        showDeleteConfirmation={showDeleteConfirmation}
        setShowDeleteConfirmation={setShowDeleteConfirmation}
      />
    ),
  };

  const productsPanel: PanelProps = {
    menuItem: translate("products"),
    className: classes.uiTab,
    key: "products",
    loading: fetchingPurchaseOrder,
    skeleton: () => <SkeletonProducts />,
    hasError: fieldHasError("products"),
    render: () => (
      <Products
        products={purchaseOrder?.purchaseOrderProducts ?? []}
        updateOrder={handleUpdatePurchaseOrder}
        viewOnly={viewOnly}
        supplierView={supplierView}
        portCodes={portCodes}
        loadingPorts={isLoadingPorts}
        currenciesResponse={currenciesResponse}
        purchaseOrder={purchaseOrder}
      />
    ),
    "data-cy": "products-tab",
  };

  const leftPanels: PanelProps[] = [detailsPanel, productsPanel];

  if (purchaseOrderId) {
    // const communicationsPanel: PanelProps = {
    //   menuItem: "Communications",
    //   key: "communications",
    //   className: classes.uiTabCommunication,
    //   render: () => (
    //     <Communication originId={purchaseOrder?.id ?? 0} originTable="purchase_order" />
    //   ),
    //   "data-cy": "communications-tab",
    // };

    const ssccPanel: PanelProps = {
      menuItem: translate("sscc"),
      className: classes.uiTab,
      key: "sscc",
      loading: fetchingPurchaseOrder || loadingOrderCartons,
      skeleton: () => <SkeletonProducts />,
      render: () => (
        <OrderSSCC
          cartons={cartons}
          products={purchaseOrder?.purchaseOrderProducts ?? []}
          updateCartons={(cartons) => setCartons(cartons)}
          purchaseOrder={purchaseOrder}
          loadingCartons={loadingOrderCartons}
          viewOnly={{
            fields: viewOnly || isFMVersion,
            actions: viewOnly || isFMVersion,
          }}
          viewSupplier={supplierView}
          calledFrom={CartonSSCCFrom.PURCHASE_ORDER}
          isLengthMandatory={isDestinationPortFromBrazil}
        />
      ),
      "data-cy": "sscc-tab",
    };

    leftPanels.push(ssccPanel);

    // leftPanels.push(communicationsPanel);
  }

  return (
    <div>
      <TitlePage
        // title={translate("order")}
        right={
          <Grid item xs={12} sm={6} md={6} lg={6} className={classes.titleDataContainer}>
            {purchaseOrder?.status && (
              <StatusBadge value={purchaseOrder?.status} className={classes.titleStatusBadge} />
            )}
            <span className={classes.titleData}>
              {purchaseOrder?.reference ? ` ${translate("order")} #${purchaseOrder.reference}` : ""}
            </span>
          </Grid>
        }
      />
      <div className={classes.maintenanceDiv}>
        {/* {titlePage()} */}
        <div className={classes.ordersTab}>
          <UITabMaterial
            leftPanels={leftPanels}
            activityPanels={leftPanels}
            showRightPanel={false}
            showActivityButton={false}
            onChangeLeftPanel={(event, panel, key) => key && setLeftPanelSelected(key)}
            tabFooter={
              <OrderActionButtons
                id={purchaseOrder?.id}
                onCancel={onCancel}
                loading={isUpdatingAction || deletingPurchaseOrder}
                viewOnly={viewOnly}
                onSave={validateAndSavePurchaseOrder}
                showModal={setShowDeleteConfirmation}
                leftPanelSelected={leftPanelSelected}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

const OrderMaintenance = () => {
  return (
    <PurchaseOrderProvider>
      <SupplierProvider>
        <OrderMaintenanceScreen />
      </SupplierProvider>
    </PurchaseOrderProvider>
  );
};

export default OrderMaintenance;
